import { Database } from "../supabase/supabaseSchema";
import { UpdateUserRow, UserRow, UserRowWithMutuals } from "./user.type";

import {
  BedSingle,
  CircleUser,
  CircleUserRound,
  Heart,
  Stethoscope,
  UserPlus,
  Users,
  UserCheck,
} from "@tamagui/lucide-icons";

export type RelatedUserRole =
  | "parent"
  | "child"
  | "spouse"
  | "doctor"
  | "patient"
  | "friend"
  | "cousin"
  | "other"
  | "grandparent"
  | "grandchild"
  | "aunt"
  | "uncle"
  | "nephew"
  | "niece";

export type ContactWithReciprocal = {
  contact: RelationshipRow;
  reciprocal: RelationshipRow;
  mutuals: RelationshipRow[];
};

export type RelationshipRow =
  Database["public"]["Tables"]["user_relationships"]["Row"] & {
    related_user: Partial<UserRowWithMutuals>;
  };

export type UpdateRelationshipRow =
  Database["public"]["Tables"]["user_relationships"]["Update"];

export type CreateRelationshipRow = {
  related_user: Partial<UserRow>;
  related_user_role?: string;
  record_view?: boolean;
  relationship_type?: string;
  user_role?: string;
  user_view?: boolean;
  user_edit?: boolean;
  preferred_contact_method?: "sms" | "email";
};

export const RELATIONSHIP_TYPES = [
  {
    type: "parent",
    reciprocal: "child",
    icon: <CircleUserRound size="$1" color="$terracotta600" />,
  },
  {
    type: "child",
    reciprocal: "parent",
    icon: <CircleUser size="$1" color="$terracotta600" />,
  },
  {
    type: "spouse",
    reciprocal: "spouse",
    icon: <Heart size="$1" color="$terracotta600" />,
  },
  {
    type: "doctor",
    reciprocal: "patient",
    icon: <Stethoscope size="$1" color="$terracotta600" />,
  },
  {
    type: "patient",
    reciprocal: "doctor",
    icon: <BedSingle size="$1" color="$terracotta600" />,
  },
  {
    type: "friend",
    reciprocal: "friend",
    icon: <Users size="$1" color="$terracotta600" />,
  },
  {
    type: "sibling",
    reciprocal: "sibling",
    icon: <UserCheck size="$1" color="$terracotta600" />,
  },
  {
    type: "cousin",
    reciprocal: "cousin",
    icon: <Users size="$1" color="$terracotta600" />,
  },
  { type: "grandparent", reciprocal: "grandchild" },
  { type: "grandchild", reciprocal: "grandparent" },
  { type: "aunt", reciprocal: "nephew" },
  { type: "uncle", reciprocal: "nephew" },
  { type: "nephew", reciprocal: "aunt" },
  { type: "niece", reciprocal: "aunt" },
  {
    type: "other",
    reciprocal: "other",
    icon: <UserPlus size="$1" color="$terracotta600" />,
  },
];

export type CreateInvite = {
  relationshipId: string;
  relatedAccountsAccessIds: string[];
};

export type ContactRow = RelationshipRow;

export type UpdateContactRow = {
  id: string;
  related_user?: UpdateUserRow;
  record_view?: boolean;
  related_user_role?: string;
  user_role?: string;
  relationship_type?: RelatedUserRole;
};

export type CreateContactRow = {
  record_view?: boolean;
  related_user_role?: string;
  user_role?: string;
  relationship_type?: RelatedUserRole;
  related_user_first_name: string;
  related_user_last_name: string;
  related_user_email: string;
  related_user_phone: string;
};

export type AddRelatedUser = {
  contactUserId: string;
  relatedUserId: string;
  relatedUserRole: string;
};

export enum ViewOrEditAccess {
  VIEW = "view",
  EDIT = "edit",
}

export type AccessChange = {
  accessType: "user" | "medical";
  accessMode: "request" | "revoke" | "give";
  actionType: ViewOrEditAccess;
};
